import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Uncaught application error:", error, errorInfo);
        this.setState({ errorInfo });
        Sentry.captureException(error, {
            contexts: { react: { componentStack: errorInfo.componentStack } },
        });
    }

    render() {
        if (this.state.hasError) {
            // Custom fallback UI for full-app crashes
            return (
                <div className="min-h-screen bg-[#1e1e1e] flex flex-col items-center justify-center p-6 text-center text-[#cccccc] font-sans">
                    <div className="bg-[#252526] border border-red-500/30 p-8 rounded-xl shadow-2xl max-w-2xl w-full flex flex-col items-center">
                        <div className="w-16 h-16 bg-red-500/10 flex items-center justify-center rounded-full mb-6">
                            <AlertTriangle className="text-red-500 w-8 h-8" />
                        </div>

                        <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
                        <p className="text-[#808080] mb-8 max-w-md">
                            A critical error occurred in the application. We've caught the crash to prevent data loss, but you may need to reload the page to continue safely.
                        </p>

                        {this.state.error && (
                            <div className="w-full bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg p-4 mb-8 text-left overflow-auto max-h-48 text-sm font-mono text-red-400 custom-scrollbar">
                                {this.state.error.toString()}
                                {this.state.errorInfo && this.state.errorInfo.componentStack && (
                                    <div className="mt-2 text-[#808080] whitespace-pre">
                                        {this.state.errorInfo.componentStack}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex gap-4">
                            <button
                                onClick={() => window.location.reload()}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
                            >
                                <RefreshCw size={18} />
                                Reload Application
                            </button>
                            <button
                                onClick={() => {
                                    localStorage.removeItem('driftops_last_view');
                                    window.location.reload();
                                }}
                                className="flex items-center gap-2 bg-transparent hover:bg-[#3c3c3c] bg-[#2a2a2a] text-[#cccccc] border border-[#4c4c4c] px-6 py-2.5 rounded-lg font-medium transition-colors"
                                title="If reloading doesn't work, this will clear your current view state and return to the main dashboard."
                            >
                                Safe Mode Reload
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
